import React, { useContext, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import { Form, Button } from 'components';
import { Container } from 'layouts';
import Designations from './formParts/Designations';
import Dimensions from './formParts/Dimensions';
import formsConfig from './formsConfig';
import { AuthContext, DialogContext, ProductsContext } from 'contexts';
import config from '../../../config';
import Dimensionsnew from "./formParts/Dimensionsnew";

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
  },
  dimensions: {
    margin: theme.spacing(3, 0, 3),
  },
  designations: {
    margin: theme.spacing(5, 0, 3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  templateBtnsList: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    columnGap: '15px',
    rowGap: '15px',
  },
  templateBtn: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    textTransform: 'inherit',
    fontSize: '0.9rem',
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    cursor: 'pointer',
  },
  templateBtnText: {
    display: 'block',
    marginTop: '10px',
  },
  selectValueOption: {
    color: theme.palette.grey[500], // Светло-серый цвет
    fontStyle: 'italic',
  },
}));

const ProductFormGenerator = (props) => {
  const classes = useStyles();

  const {
    state: { roles },
    actions: authActions,
  } = useContext(AuthContext);
  const { actions: productsActions } = useContext(ProductsContext);
  const { actions: dialogActions } = useContext(DialogContext);
  const { enqueueSnackbar } = useSnackbar();
  const { bu, category, productName } = props.match.params;

  useEffect(() => {
    if (!roles.length) {
      authActions.fetchRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formConfig = formsConfig[bu][category];
  const formConfigDocDomain = formsConfig[bu].documentsDomain;
  const datasheetTemplates = formsConfig[bu][category].datasheetTemplates;

  const createTemplateButton = (templateId) => (
    <div
      key={templateId}
      onClick={() => {
        props.history.push(`/products/${bu}/${category}/${productName}/templates/${templateId}`);
      }}
      className={classes.templateBtn}
    >
      <img style={{ width: 100, height: 100 }} src={`/templates/${templateId}.png`} />
      <span className={classes.templateBtnText}>Template {templateId}</span>
    </div>
  );

  const actions = {
    createNewDatasheet: () => {
      dialogActions.open({
        title: 'Choose Template Design',
        content: (
          <div className={classes.templateBtnsList}>
            {/* [8, 7, 6, 5, 4, 3, 2, 1].map((e) => createTemplateButton(e))  */}
            {datasheetTemplates.map((e) => createTemplateButton(e))}
          </div>
        ),
        acceptBtnText: 'Close',
      });
    },
    editExistingDatasheet: () => {
      props.history.push(
        `/products/${bu}/${category}/${productName}/templates/${props.editValues?.template?.id}/edit`,
      );
    },
    getDocumentLink: () => {
      return props.editValues ? props.editValues.documentUrl : null;
    },
  };

  const successNotification = () =>
    enqueueSnackbar('The Product Saved', {
      variant: 'success',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });

  const formik = useFormik({
    initialValues: { ...formConfig.initialValues, ...props.editValues },
    validationSchema: formConfig.validationSchema,
    onSubmit: (values) => {
      if (formConfig.onSubmit) formConfig.onSubmit(values);

      // if there is no id, create new product
      if (values._id) {
        productsActions.updateProduct(values._id, bu, category, values, () => {
          if(formik.values.sku){
            props.history.replace(
              `/products/${bu}/${category}/by-sku/${encodeURIComponent(formik.values.sku)}/edit`,
            );
          } else {
            props.history.replace(
              `/products/${bu}/${category}/${encodeURIComponent(formik.values.name)}/edit`,
            );
          }
          productsActions.getProducts();
          successNotification();
        });
      } else {
        productsActions.createProduct(bu, category, values, () => {
          formik.resetForm({
            ...formConfig.initialValues,
            ...props.editValues,
          });
          productsActions.getProducts();
          successNotification();
        });
      }
    },
  });

  const getItems = (specifier) => {
    const options = props.getSpecifierValues(specifier).map((v) => ({
      id: v.name,
      label: v.name,
    }));
    //

    return [
      {
        id: '', // value
        label: 'None',
        isDefault: true, // Флаг, указывающий, что это первая опция
      },
      ...options.map((option) => ({ ...option, isDefault: false })),
    ];
  };

  const canEdit = roles.includes('admin') || roles.includes(`${bu}_edit`);

  //console.log('ProductFormGenerator:', bu, roles);
  const isDuplicate = props.editValues && props.editValues.isDuplicate ? true : false;
  return (
    <Form className={classes.form} onSubmit={formik.handleSubmit} noValidate>
      <Container maxWidth="sm">
        {formConfig.formFields.map((field) => {
          const InputComponent = Form[field.component];
          const isSelectWithMultiple = field.component === 'Select' && field.props.multiple;
          return (
            <InputComponent
              key={field.props.id}
              fullWidth
              margin="dense"
              disabled={!canEdit}
              editDatasheet={
                roles.includes(`${bu}_create_datasheet`) && props.editValues?.template?.id
              }
              createDatasheet={roles.includes(`${bu}_create_datasheet`)}
              items={getItems(field.props.name.split('.').pop())}
              {...field.props}
              value={
                field.props.value
                  ? field.props.value(formik) //
                  : isDuplicate && field.props.name === 'name' // duplicate
                  ? formik.values[field.props.name] + config.TEXT_DUPLICATE //
                  : formik.values[field.props.name] || ''
              }
              createonclick={actions[field.props.createBtnAction]}
              editonclick={actions[field.props.editBtnAction]}
              viewonclick={actions[field.props.viewBtnAction]}
              onChange={field.props.onChange ? field.props.onChange(formik) : formik.handleChange}
              onLoad={field.props.onLoad ? field.props.onLoad(formik) : null}
              autoFocus={false}
              error={Boolean(formik.errors[field.props.id] && formik.touched[field.props.id])}
              helperText={
                formik.errors[field.props.id] && formik.touched[field.props.id]
                  ? formik.errors[field.props.id]
                  : false
              }
              documentsDomain={formConfigDocDomain}
            />
          );
        })}
      </Container>

      {formConfig.dimensions && bu == 'vpgt' ? (
          <Dimensionsnew formik={formik} disabled={!canEdit} className={classes.dimensions}  fields={[
            { id: 'width.global', label: 'Width' },
            { id: 'height.global', label: 'Height' },
            { id: 'length.global', label: 'Length' },
          ]} units={['mm']}/>
      ) : null}
      {formConfig.dimensions && bu != 'vpgt' ? (
          <Dimensions formik={formik} disabled={!canEdit} className={classes.dimensions} />
      ) : null}

      {formConfig.designations ? (
        <Designations
          formik={formik}
          className={classes.designations}
          disabled={!canEdit}
          rows={Object.keys(formik.values.designations).map((designation) => {
            return {
              ...formik.values.designations[designation],
              designation,
            };
          })}
          {...formConfig.designations.props}
        />
      ) : null}

      {canEdit && (
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
        >
          Save Product
        </Button>
      )}
    </Form>
  );
};

export default withRouter(ProductFormGenerator);
